import React from "react";
import { Alert, AlertTitle, Box, Button, Icon, Input, Text } from "@salestrak/ui";
import { useRouter } from "#app/lib/hooks/router-compat";
import { requestPasswordReset } from "#app/lib/auth-service";
import { useSplashAutoFocus } from "#app/lib/splash-context";
import { validateEmail } from "#app/lib/custom-field-utils";
import { AnalyticsContext, AuthEvent, trackEvent } from "#app/lib/analytics";

export function ForgotPasswordForm() {
  const router = useRouter();
  const [email, setEmail] = React.useState("");
  // Field errors sit under the field; a failed request is not the field's
  // fault and reads in the form-level Alert above the CTA.
  const [emailError, setEmailError] = React.useState("");
  const [formError, setFormError] = React.useState("");
  const [isLoading, setIsLoading] = React.useState(false);
  const emailRef = useSplashAutoFocus();

  const submit = async () => {
    setFormError("");
    if (!email.trim()) {
      setEmailError("Email is required.");
      return;
    }
    // The form is noValidate (the native bubble clashes with the inline
    // errors), so the format check lives here.
    if (!validateEmail(email.trim())) {
      setEmailError("Enter a valid email address.");
      return;
    }
    setIsLoading(true);
    try {
      await requestPasswordReset(email.trim());
      trackEvent({ context: AnalyticsContext.Auth, event: AuthEvent.PasswordResetRequest, label: "success" });
      router.replace({
        pathname: "/(auth)/reset-password-sent",
        params: { email: email.trim() },
      });
    } catch (err: any) {
      trackEvent({ context: AnalyticsContext.Auth, event: AuthEvent.PasswordResetRequest, label: "failure" });
      setFormError(err.message ?? "Something went wrong. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    void submit();
  };

  return (
    <Box as="form" noValidate direction="column" gap="lg" onSubmit={handleSubmit}>
      <Box gap="base">
        <Text
          as="label"
          htmlFor="forgot-email"
          size="sm"
          weight="medium"
          variant="muted"
        >
          Email
        </Text>
        <Box gap="sm">
          <Input
            id="forgot-email"
            size="xl"
            type="email"
            placeholder="name@company.com"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setEmailError("");
              setFormError("");
            }}
            aria-invalid={!!emailError}
            ref={emailRef}
            autoComplete="email"
            disabled={isLoading}
          />
          {emailError ? (
            <Text size="xs" weight="medium" variant="destructive">
              {emailError}
            </Text>
          ) : null}
        </Box>
      </Box>

      {formError ? (
        <Alert variant="error">
          <Icon name="alert-circle" />
          <AlertTitle>{formError}</AlertTitle>
        </Alert>
      ) : null}

      <Box direction="column" paddingTop="lg">
        <Button type="submit" size="xl" loading={isLoading}>
          Send Reset Link
        </Button>
      </Box>
    </Box>
  );
}
